import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { Numeric, Typography } from '@blade/ui-kit'
import { Trend } from '@root/entities/trend/model'
import { trendUpDownDataColorCss } from '@root/entities/trend/ui/trend-ui-utils'
import { useDateFormat } from '@root/shared/localization'

import type { ClassNameProps } from '@root/shared/react'
import type { BigNumber } from 'bignumber.js'

type Props = ClassNameProps & {
  pnlUsd: BigNumber | undefined
  date: Date
}

export const PnlCalendarTooltipContent: React.FC<Props> = ({
  className,
  pnlUsd,
  date,
}) => {
  const { t } = useTranslation()
  const { formatDate } = useDateFormat()
  const formatted = useMemo(
    () => formatDate(date, 'MMM d, yyyy'),
    [date, formatDate],
  )
  return (
    <Root className={className}>
      <Title variant="body4-semi-bold">{formatted}</Title>
      {pnlUsd ? (
        <Item>
          <Typography>{t('common.pnl')}</Typography>
          <Value data-trend={Trend.of(pnlUsd)}>
            <Numeric
              preset="currency-amount-short"
              value={pnlUsd}
              forceSign
              currency="$"
            />
          </Value>
        </Item>
      ) : undefined}
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  gap: 8px;
  min-width: 100px;
`

const Title = styled(Typography)``

const Item = styled.div`
  display: grid;
  grid-auto-flow: column;
  justify-content: space-between;
  color: var(--text-11);
`

const Value = styled(Typography).attrs({ variant: 'body4-semi-bold' })`
  ${trendUpDownDataColorCss};
`
